import React from "react";
import { format } from "date-fns";
import { motion } from "framer-motion";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import TableSkeleton from "@/shared/components/shared/TableSkeleton";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import { Clock, Eye, Copy, Check } from "lucide-react";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { TransactionStatusBadge } from "../transactions/table-components/TransactionStatusBadge";
import { TransactionTypeIcon } from "../transactions/table-components/TransactionTypeIcon";
import { formatAmountWithCurrency } from "../transactions/table-components/utils";

const TransactionRow = React.memo(
  ({
    tx,
    index,
    onView,
    showMerchant,
    showReseller,
    showEngine,
    mixedTypes,
    datePattern,
    t,
  }) => {
    const [isCopied, setIsCopied] = React.useState(false);
    const isWithdrawal = mixedTypes && tx.type?.toLowerCase() === "withdrawal";

    const handleCopy = (e) => {
      e.stopPropagation();
      if (!tx.id) return;
      navigator.clipboard.writeText(tx.id);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    };

    const cells = [
      {
        id: "tx",
        className: "py-3",
        content: (
          <div className="flex items-center gap-3">
            <TransactionTypeIcon isWithdrawal={isWithdrawal} size="h-4 w-4" />
            <div className="min-w-0">
              <div className="flex items-center gap-1">
                <p className="font-mono text-[11px] font-medium text-slate-500 truncate max-w-[100px]">
                  {tx.id?.slice(0, 12) ?? "—"}
                </p>
                <button
                  type="button"
                  onClick={handleCopy}
                  className={`p-1 rounded-md transition-all ${
                    isCopied
                      ? "bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100"
                      : "text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                  }`}
                >
                  {isCopied ? (
                    <Check className="h-3 w-3" />
                  ) : (
                    <Copy className="h-3 w-3" />
                  )}
                </button>
              </div>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tight mt-0.5">
                {t ? t(tx.type?.toLowerCase()) : tx.type}
              </p>
            </div>
          </div>
        ),
      },
      showEngine && {
        id: "engine",
        className: "py-3",
        content: (
          <span className="text-sm font-medium text-slate-700 truncate block max-w-[120px]">
            {tx.engine?.name ?? tx.engine_name ?? "—"}
          </span>
        ),
      },
      showReseller && {
        id: "reseller",
        className: "py-3",
        content: (
          <span className="text-sm font-medium text-slate-700 truncate block max-w-[120px]">
            {tx.reseller?.name ?? tx.reseller_name ?? "—"}
          </span>
        ),
      },
      showMerchant && {
        id: "merchant",
        className: "py-3",
        content: (
          <span className="text-sm font-medium text-slate-700 truncate block max-w-[120px]">
            {tx.merchant?.name ?? tx.merchant_name ?? "—"}
          </span>
        ),
      },
      {
        id: "provider",
        className: "py-3",
        content: (
          <span className="text-sm text-slate-600 font-medium">
            {tx.provider?.name ?? tx.provider_code ?? "—"}
          </span>
        ),
      },
      {
        id: "amount",
        className: "py-3 text-center",
        content: (
          <span className="font-bold text-slate-900">
            {formatAmountWithCurrency(tx.amount, tx.currency)}
          </span>
        ),
      },
      {
        id: "fee",
        className: "py-3 text-center",
        content: (
          <span className="font-medium text-red-500">
            {formatAmountWithCurrency(tx.feeAmount, tx.currency)}
          </span>
        ),
      },
      {
        id: "net",
        className: "py-3 text-center",
        content: (
          <span className="font-bold text-emerald-600">
            {formatAmountWithCurrency(tx.netAmount, tx.currency)}
          </span>
        ),
      },
      {
        id: "currency",
        className: "py-3 text-center",
        content: (
          <Badge
            variant="outline"
            className="text-[10px] font-bold uppercase border-slate-200 text-slate-500 bg-slate-50/50"
          >
            {tx.currency ?? "—"}
          </Badge>
        ),
      },
      {
        id: "status",
        className: "py-3",
        content: <TransactionStatusBadge status={tx.status} t={t} />,
      },
      {
        id: "date",
        className: "py-3",
        content: (
          <div className="flex flex-col">
            <span className="text-[13px] font-medium text-slate-700 whitespace-nowrap">
              {tx.createdAt ? format(new Date(tx.createdAt), datePattern) : "—"}
            </span>
          </div>
        ),
      },
      {
        id: "action",
        className: "py-3 text-right",
        content: (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-slate-400 hover:text-blue-600 hover:bg-blue-50"
          >
            <Eye className="h-4 w-4" />
          </Button>
        ),
      },
    ].filter(Boolean);

    return (
      <motion.tr
        initial={{ opacity: 0, y: 5 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.02 * index }}
        className="hover:bg-slate-50 cursor-pointer transition-colors border-b border-slate-50 last:border-0"
        onClick={() => onView(tx)}
      >
        {cells.map((cell) => (
          <TableCell key={cell.id} className={cell.className}>
            {cell.content}
          </TableCell>
        ))}
      </motion.tr>
    );
  },
);

export default function AdminTransactionTable({
  transactions = [],
  isLoading,
  onView,
  showMerchant = true,
  showReseller = false,
  showEngine = false,
  compact = false,
  mixedTypes = false,
}) {
  const { t } = useLanguage();

  const columns = React.useMemo(() => {
    const cols = [
      { header: t?.("transaction") ?? "Transaction", width: "200px" },
      showEngine && { header: t?.("engine") ?? "Engine" },
      showReseller && { header: t?.("reseller") ?? "Reseller" },
      showMerchant && { header: t?.("merchant") ?? "Merchant" },
      { header: t?.("provider") ?? "Provider" },
      { header: t?.("amount") ?? "Amount", className: "text-center" },
      { header: t?.("fee") ?? "Fee", className: "text-center" },
      { header: t?.("netAmount") ?? "Net", className: "text-center" },
      { header: t?.("currency") ?? "Currency", className: "text-center" },
      { header: t?.("status") ?? "Status" },
      { header: t?.("date") ?? "Date" },
      { header: "", className: "text-right" },
    ];
    return cols.filter(Boolean);
  }, [t, showEngine, showReseller, showMerchant]);

  if (!isLoading && transactions.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-12 text-center">
        <div className="mx-auto w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-4">
          <Clock className="h-8 w-8 text-slate-300" />
        </div>
        <h3 className="text-lg font-semibold text-slate-900">
          {t?.("noTransactionsYet") ?? "No transactions yet"}
        </h3>
        <p className="text-slate-500 mt-1">
          {t?.("noTransactionsFound") ??
            "No transactions found matching your criteria"}
        </p>
      </div>
    );
  }

  const datePattern = compact ? "MMM d, HH:mm" : "MMM d, yyyy";

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
      <div className="overflow-x-auto scrollbar-thin">
        {isLoading ? (
          <TableSkeleton columns={columns} rows={8} />
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/50 border-b border-slate-100 hover:bg-transparent">
                {columns.map((col, i) => (
                  <TableHead
                    key={i}
                    className={`h-11 py-0 font-bold text-slate-500 text-[11px] uppercase tracking-wider ${col.className ?? ""}`}
                    style={col.width ? { width: col.width } : {}}
                  >
                    {col.header}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {transactions.map((tx, index) => (
                <TransactionRow
                  key={tx.id || index}
                  tx={tx}
                  index={index}
                  onView={onView}
                  showMerchant={showMerchant}
                  showReseller={showReseller}
                  showEngine={showEngine}
                  mixedTypes={mixedTypes}
                  datePattern={datePattern}
                  compact={compact}
                  t={t}
                />
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
